import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
} from 'react';
import { Columns3, GripVertical, Lock, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { rowShift, useListReorderDrag } from '../hooks/useListReorderDrag';

export interface ColumnsPanelItem {
  id: string;
  label: string;
  visible: boolean;
  /** Turning this column off is not allowed (it would hide every column). */
  hideDisabled?: boolean;
}

export interface ColumnsPanelProps {
  /** Customizable columns, in the current order. */
  items: ColumnsPanelItem[];
  /** Always-visible columns listed above the rest (e.g. the row selector). */
  lockedLabels?: string[];
  onToggle: (id: string, visible: boolean) => void;
  onMove: (from: number, to: number) => void;
  onReset: () => void;
}

/**
 * KIR-4089 "Columns" toolbar button + panel: toggle each column, drag (or
 * Arrow Up/Down on the handle) to reorder, and reset to the default layout.
 */
export function ColumnsPanel({
  items,
  lockedLabels = [],
  onToggle,
  onMove,
  onReset,
}: ColumnsPanelProps): ReactElement {
  // Screen-reader announcement for keyboard reorders.
  const [announcement, setAnnouncement] = useState('');
  // Keyboard reorder moves the handle's DOM node, which drops focus; restore
  // it on the moved handle once the reordered list commits.
  const pendingFocusId = useRef<string | null>(null);
  const handleRefs = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    if (!pendingFocusId.current) return;
    handleRefs.current.get(pendingFocusId.current)?.focus();
    pendingFocusId.current = null;
  }, [items]);

  const visibleCount = items.filter(item => item.visible).length;

  // Pointer drag from the handle: the row lifts and follows the pointer while
  // the rows it passes slide aside (no browser drag ghost).
  const listRef = useRef<HTMLUListElement>(null);
  const rowRefs = useRef(new Map<string, HTMLLIElement>());
  const { drag, startDrag } = useListReorderDrag({
    items,
    getRowElements: () => items.map(item => rowRefs.current.get(item.id)),
    scrollContainerRef: listRef,
    onDrop: (from, to) => {
      setAnnouncement(
        `${items[from].label} moved to position ${to + 1} of ${items.length}`
      );
      onMove(from, to);
    },
  });

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
    id: string
  ): void => {
    // A pointer drag owns the order until it ends.
    if (drag) return;
    const delta =
      event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
    const target = index + delta;
    if (delta === 0 || target < 0 || target >= items.length) return;
    event.preventDefault();
    pendingFocusId.current = id;
    setAnnouncement(
      `${items[index].label} moved to position ${target + 1} of ${items.length}`
    );
    onMove(index, target);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 shadow-sm"
          aria-label="Customize columns"
        >
          <Columns3 className="mr-2 h-3.5 w-3.5" />
          <span className="text-xs font-medium">Columns</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-0"
        aria-label="Columns"
        role="dialog"
      >
        <div className="flex items-center justify-between px-3 pt-3">
          <span className="text-sm font-semibold">Columns</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-primary"
            onClick={onReset}
          >
            <RotateCcw className="mr-1 h-3 w-3" />
            Reset to default
          </Button>
        </div>
        <p className="px-3 pb-2 text-xs text-muted-foreground">
          Drag to reorder. Toggle to show or hide.
        </p>
        <ul
          ref={listRef}
          className="max-h-[60vh] overflow-y-auto overflow-x-hidden border-t py-1"
        >
          {lockedLabels.map(label => (
            <li
              key={label}
              className="flex items-center gap-2 px-3 py-1.5 text-sm"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground/30" />
              <span className="flex-1">{label}</span>
              <Lock
                className="h-3.5 w-3.5 text-muted-foreground"
                role="img"
                aria-label="Locked"
              />
              <Switch checked disabled aria-label={`Show ${label}`} />
            </li>
          ))}
          {items.map((item, index) => (
            <li
              key={item.id}
              ref={element => {
                if (element) rowRefs.current.set(item.id, element);
                else rowRefs.current.delete(item.id);
              }}
              data-testid={`columns-panel-item-${item.id}`}
              data-dragging={drag?.from === index || undefined}
              style={
                drag
                  ? {
                      transform: `translateY(${
                        drag.from === index
                          ? drag.offset
                          : rowShift(index, drag)
                      }px)`,
                    }
                  : undefined
              }
              className={cn(
                'relative flex items-center gap-2 bg-popover px-3 py-1.5 text-sm',
                // Rows the dragged one passes glide aside; the dragged row
                // tracks the pointer 1:1 (no transition) and floats above.
                drag &&
                  drag.from !== index &&
                  'transition-transform duration-150 ease-out motion-reduce:transition-none',
                drag?.from === index &&
                  'z-10 cursor-grabbing rounded-md shadow-lg ring-1 ring-border'
              )}
            >
              <button
                type="button"
                ref={element => {
                  if (element) handleRefs.current.set(item.id, element);
                  else handleRefs.current.delete(item.id);
                }}
                aria-label={`Move ${item.label}`}
                title="Drag, or use Arrow Up / Arrow Down, to reorder"
                className="cursor-grab touch-none rounded text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing"
                onKeyDown={event => handleKeyDown(event, index, item.id)}
                onPointerDown={event => startDrag(index, event)}
              >
                <GripVertical className="h-4 w-4" />
              </button>
              <span
                className={cn(
                  'flex-1 truncate',
                  !item.visible && 'text-muted-foreground'
                )}
              >
                {item.label}
              </span>
              <Switch
                checked={item.visible}
                // Never let the user hide every column.
                disabled={
                  item.visible && (visibleCount === 1 || item.hideDisabled)
                }
                onCheckedChange={checked => onToggle(item.id, checked)}
                aria-label={`Show ${item.label}`}
              />
            </li>
          ))}
        </ul>
        <span className="sr-only" aria-live="polite">
          {announcement}
        </span>
      </PopoverContent>
    </Popover>
  );
}
